/**
 * @vueform/multiselect puts invalid ARIA on combobox roots and search inputs
 * (`aria-autocomplete`, `aria-multiselectable`, `aria-placeholder`) — fails axe / Lighthouse.
 */
export function sanitizeVueformMultiselectA11y(
  root: ParentNode = document,
): void {
  root.querySelectorAll(".multiselect[aria-autocomplete]").forEach((el) => {
    el.removeAttribute("aria-autocomplete");
  });

  root.querySelectorAll('[role="combobox"]').forEach((el) => {
    el.removeAttribute("aria-multiselectable");
    el.removeAttribute("aria-placeholder");
  });
}

const INVALID_COMBOBOX_ATTRS = ["aria-multiselectable", "aria-placeholder"];
const INVALID_MULTISELECT_ATTRS = ["aria-autocomplete"];

function stripInvalidMultiselectAttrs(mutations: MutationRecord[]): void {
  for (const mutation of mutations) {
    if (mutation.type === "childList") {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          sanitizeVueformMultiselectA11y(node);
        }
      });
      continue;
    }

    if (mutation.type !== "attributes") continue;

    const target = mutation.target;
    if (!(target instanceof Element)) continue;

    if (
      mutation.attributeName &&
      INVALID_MULTISELECT_ATTRS.includes(mutation.attributeName) &&
      target.classList.contains("multiselect")
    ) {
      target.removeAttribute(mutation.attributeName);
      continue;
    }

    if (
      mutation.attributeName &&
      INVALID_COMBOBOX_ATTRS.includes(mutation.attributeName) &&
      target.getAttribute("role") === "combobox"
    ) {
      target.removeAttribute(mutation.attributeName);
    }
  }
}

const observers = new WeakMap<ParentNode, MutationObserver>();

/** Keep stripping invalid multiselect ARIA when Vueform re-applies it after updates. */
export function observeVueformMultiselectA11y(
  root: ParentNode = document,
): void {
  if (observers.has(root)) return;

  sanitizeVueformMultiselectA11y(root);

  const observer = new MutationObserver((mutations) => {
    stripInvalidMultiselectAttrs(mutations);
  });

  observer.observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [...INVALID_COMBOBOX_ATTRS, ...INVALID_MULTISELECT_ATTRS],
  });

  observers.set(root, observer);
}

export function disconnectVueformMultiselectA11y(root: ParentNode): void {
  observers.get(root)?.disconnect();
  observers.delete(root);
}
